/**
 * Pattern Wheel - 24h × 7day radial polar visualization
 *
 * Concentric rings = days of week (innermost Sun → outermost Sat)
 * Sectors = hours of day (0 at top, clockwise)
 * Cell color intensity = activity count
 */
import React, { useMemo, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { TemporalHeatmapData } from '../services/polService';

interface PatternWheelProps {
  data: TemporalHeatmapData[];
  size?: number;
  title?: string;
  onCellClick?: (hour: number, day: number) => void;
  selectedCell?: { hour: number; day: number } | null;
}

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const PatternWheel: React.FC<PatternWheelProps> = ({ data, size = 440, onCellClick, selectedCell }) => {
  const [hover, setHover] = useState<{ hour: number; day: number; count: number } | null>(null);

  // Build 24 x 7 matrix
  const matrix = useMemo(() => {
    const m: number[][] = Array.from({ length: 7 }, () => Array(24).fill(0));
    data.forEach((d) => {
      if (d.day_of_week >= 0 && d.day_of_week < 7 && d.hour >= 0 && d.hour < 24) {
        m[d.day_of_week][d.hour] = d.activity_count;
      }
    });
    return m;
  }, [data]);

  const filteredMatrix = matrix;

  const maxCount = useMemo(() => {
    let max = 0;
    filteredMatrix.forEach((row) => row.forEach((v) => { if (v > max) max = v; }));
    return max || 1;
  }, [filteredMatrix]);

  const { totalActivity, peakHour, peakDay, hourTotals } = useMemo(() => {
    let total = 0;
    let peakCount = 0;
    let pHour = 0;
    let pDay = 0;
    const hTotals = Array(24).fill(0);
    filteredMatrix.forEach((row, d) => {
      row.forEach((v, h) => {
        total += v;
        hTotals[h] += v;
        if (v > peakCount) {
          peakCount = v;
          pHour = h;
          pDay = d;
        }
      });
    });
    return { totalActivity: total, peakHour: pHour, peakDay: pDay, hourTotals: hTotals };
  }, [filteredMatrix]);

  const peakHourTotal = hourTotals.indexOf(Math.max(...hourTotals));

  // Geometry
  const cx = size / 2;
  const cy = size / 2;
  const outerRadius = size * 0.46;
  const innerRadius = size * 0.15;
  const ringWidth = (outerRadius - innerRadius) / 7;
  const anglePerHour = (Math.PI * 2) / 24;

  // Color scale: dark → cool → warm → hot
  const getColor = (count: number): string => {
    if (count === 0) return 'rgba(255,255,255,0.03)';
    const t = Math.min(1, count / maxCount);
    if (t < 0.25) return `rgba(88, 166, 255, ${0.3 + t * 2})`;
    if (t < 0.5) return `rgba(63, 185, 80, ${0.5 + t})`;
    if (t < 0.75) return `rgba(245, 159, 10, ${0.7 + t * 0.3})`;
    return `rgba(248, 81, 73, ${0.85 + t * 0.15})`;
  };

  // Build SVG arc path for a sector
  const arcPath = (hour: number, ring: number): string => {
    const startAngle = hour * anglePerHour - Math.PI / 2;
    const endAngle = (hour + 1) * anglePerHour - Math.PI / 2;
    const r1 = innerRadius + ring * ringWidth;
    const r2 = innerRadius + (ring + 1) * ringWidth;

    const x1 = cx + r1 * Math.cos(startAngle);
    const y1 = cy + r1 * Math.sin(startAngle);
    const x2 = cx + r2 * Math.cos(startAngle);
    const y2 = cy + r2 * Math.sin(startAngle);
    const x3 = cx + r2 * Math.cos(endAngle);
    const y3 = cy + r2 * Math.sin(endAngle);
    const x4 = cx + r1 * Math.cos(endAngle);
    const y4 = cy + r1 * Math.sin(endAngle);

    return `M ${x1} ${y1} L ${x2} ${y2} A ${r2} ${r2} 0 0 1 ${x3} ${y3} L ${x4} ${y4} A ${r1} ${r1} 0 0 0 ${x1} ${y1} Z`;
  };

  // Hour tick positions (around the outside)
  const hourTicks = [0, 3, 6, 9, 12, 15, 18, 21];

  return (
    <Box sx={{ width: '100%' }}>

      <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', flexWrap: 'wrap', justifyContent: 'center' }}>
        {/* The Wheel */}
        <Box sx={{ position: 'relative', flexShrink: 0 }}>
          <svg width={size} height={size} style={{ overflow: 'visible' }}>
            {/* Ring backgrounds (alternating) */}
            {DAY_NAMES.map((_, ring) => (
              <circle
                key={`ring-bg-${ring}`}
                cx={cx}
                cy={cy}
                r={innerRadius + (ring + 0.5) * ringWidth}
                fill="none"
                stroke="rgba(255,255,255,0.03)"
                strokeWidth={ringWidth}
              />
            ))}

            {/* Activity cells */}
            {filteredMatrix.map((row, day) =>
              row.map((count, hour) => {
                const isPeak = hour === peakHour && day === peakDay && count > 0;
                const isHovered = hover?.hour === hour && hover?.day === day;
                const isSelected = selectedCell?.hour === hour && selectedCell?.day === day;
                let stroke = 'rgba(0,0,0,0.2)';
                let strokeWidth = 0.5;
                if (isSelected) { stroke = '#ffffff'; strokeWidth = 2.5; }
                else if (isPeak) { stroke = '#ffeb3b'; strokeWidth = 2; }
                else if (isHovered) { stroke = '#ffffff'; strokeWidth = 1.5; }
                return (
                  <path
                    key={`cell-${day}-${hour}`}
                    d={arcPath(hour, day)}
                    fill={isSelected ? 'rgba(255,255,255,0.35)' : getColor(count)}
                    stroke={stroke}
                    strokeWidth={strokeWidth}
                    style={{ cursor: count > 0 ? 'pointer' : 'default', transition: 'stroke 0.1s' }}
                    onMouseEnter={() => setHover({ hour, day, count })}
                    onMouseLeave={() => setHover(null)}
                    onClick={() => count > 0 && onCellClick?.(hour, day)}
                  />
                );
              })
            )}

            {/* Hour gridlines (every 6h emphasized) */}
            {[0, 6, 12, 18].map((h) => {
              const angle = h * anglePerHour - Math.PI / 2;
              return (
                <line
                  key={`gridline-${h}`}
                  x1={cx + (innerRadius - 4) * Math.cos(angle)}
                  y1={cy + (innerRadius - 4) * Math.sin(angle)}
                  x2={cx + (outerRadius + 4) * Math.cos(angle)}
                  y2={cy + (outerRadius + 4) * Math.sin(angle)}
                  stroke="rgba(255,255,255,0.15)"
                  strokeWidth={0.5}
                />
              );
            })}

            {/* Hour labels around the outside */}
            {hourTicks.map((h) => {
              const angle = h * anglePerHour - Math.PI / 2;
              const labelR = outerRadius + 16;
              const x = cx + labelR * Math.cos(angle);
              const y = cy + labelR * Math.sin(angle);
              const isPeakHour = h === peakHourTotal;
              return (
                <text
                  key={`hour-label-${h}`}
                  x={x}
                  y={y}
                  textAnchor="middle"
                  dominantBaseline="middle"
                  fontSize={11}
                  fontWeight={isPeakHour ? 700 : 500}
                  fill={isPeakHour ? '#ffeb3b' : 'rgba(255,255,255,0.7)'}
                >
                  {h === 0 ? '12a' : h === 12 ? '12p' : h < 12 ? `${h}a` : `${h - 12}p`}
                </text>
              );
            })}

            {/* Day labels on inner rings - short 1-letter, rotated for readability */}
            {DAY_NAMES.map((day, ring) => {
              const r = innerRadius + (ring + 0.5) * ringWidth;
              const angle = -Math.PI / 2 - 0.01; // just before 12 o'clock
              const x = cx + r * Math.cos(angle);
              const y = cy + r * Math.sin(angle);
              return (
                <text
                  key={`day-${day}`}
                  x={x}
                  y={y}
                  textAnchor="middle"
                  dominantBaseline="middle"
                  fontSize={12}
                  fontWeight={1000}
                  fill="rgba(255,255,255,0.5)"
                  style={{ pointerEvents: 'none' }}
                >
                  {day[0]}
                </text>
              );
            })}

            {/* Center circle */}
            <circle cx={cx} cy={cy} r={innerRadius - 2} fill="rgba(0,0,0,0.3)" stroke="rgba(255,255,255,0.1)" strokeWidth={1} />

            {/* Center text: total or hover value */}
            {hover ? (
              <>
                <text x={cx} y={cy - 8} textAnchor="middle" fontSize={18} fontWeight={700} fill="#ffeb3b">
                  {hover.count.toLocaleString()}
                </text>
                <text x={cx} y={cy + 10} textAnchor="middle" fontSize={9} fill="rgba(255,255,255,0.7)">
                  {DAY_NAMES[hover.day]}
                </text>
                <text x={cx} y={cy + 22} textAnchor="middle" fontSize={9} fill="rgba(255,255,255,0.5)">
                  {hover.hour.toString().padStart(2, '0')}:00
                </text>
              </>
            ) : (
              <>
                <text x={cx} y={cy - 8} textAnchor="middle" fontSize={20} fontWeight={700} fill="#58a6ff">
                  {totalActivity.toLocaleString()}
                </text>
                <text x={cx} y={cy + 12} textAnchor="middle" fontSize={9} fill="rgba(255,255,255,0.6)">
                  total events
                </text>
              </>
            )}
          </svg>
        </Box>

        {/* Vertical intensity scale beside the wheel */}
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 0.5, py: 1, alignSelf: 'center' }}>
          <Typography variant="caption" sx={{ fontSize: '0.5rem', color: 'rgba(255,255,255,0.5)', writingMode: 'vertical-rl', transform: 'rotate(180deg)', letterSpacing: 1, mb: 0.5 }}>
            HIGH
          </Typography>
          <Box sx={{
            width: 8,
            height: size * 0.5,
            borderRadius: 4,
            background: 'linear-gradient(to bottom, rgba(248,81,73,1), rgba(245,159,10,0.9), rgba(63,185,80,0.8), rgba(88,166,255,0.3))',
          }} />
          <Typography variant="caption" sx={{ fontSize: '0.5rem', color: 'rgba(255,255,255,0.5)', writingMode: 'vertical-rl', transform: 'rotate(180deg)', letterSpacing: 1, mt: 0.5 }}>
            LOW
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default PatternWheel;
